import React from 'react'
import { observer } from 'mobx-react-lite'

import Avatar from '@mui/material/Avatar'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Switch from '@mui/material/Switch'
import { useTheme } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'
import { Icon } from '@mxenabled/mx-icons'

import { Dialog, useEvent, useGlobalCopyStore, useTransactionStore } from '../../../../../common'

import type { Transaction } from '../../../../../core'

import { useTransactionsUiStore } from '../../../../context/hooks'
import { ON_CLICK_EVENTS } from '../../../../constants/OnClickEvents'

interface ExcludeActionProps {
  transaction: Transaction
}

const ExcludeAction: React.FC<ExcludeActionProps> = ({ transaction }) => {
  const { onEvent } = useEvent()
  const { setAlert, updateTransaction } = useTransactionStore()
  const { setShouldDisableDrawerScroll } = useTransactionsUiStore()
  const { common: commonCopy, transactions: copy } = useGlobalCopyStore()
  const theme = useTheme()
  const [open, setOpen] = React.useState(false)

  const isExcluded = transaction.is_hidden

  const toggleExclude = async () => {
    try {
      await updateTransaction({ ...transaction, is_hidden: !transaction.is_hidden })
      onEvent(
        isExcluded
          ? ON_CLICK_EVENTS.ON_TRANSACTION_UNHIDDEN
          : ON_CLICK_EVENTS.ON_TRANSACTION_HIDDEN,
      )
      setAlert(isExcluded ? copy.transaction_included : copy.transaction_excluded)
    } catch {
      setAlert(isExcluded ? copy.transaction_not_included : copy.transaction_not_excluded)
    }
  }

  const bgColor = theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'

  return (
    <React.Fragment>
      {transaction && (
        <ListItem className="mx-txn-hide-transaction" disableGutters={true} disablePadding={true}>
          <ListItemButton
            aria-checked={isExcluded}
            aria-labelledby="hide-transaction-text"
            onClick={() => {
              onEvent(
                isExcluded
                  ? ON_CLICK_EVENTS.ON_TRANSACTION_UNHIDE_CLICK
                  : ON_CLICK_EVENTS.ON_TRANSACTION_HIDE_CLICK,
              )
              onEvent(
                isExcluded
                  ? 'transaction_details_click_unhide_transaction'
                  : 'transaction_details_click_hide_transaction',
                {
                  transaction_guid: transaction.guid,
                },
              )
              setOpen(true)
            }}
            role="switch"
            tabIndex={0}
          >
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: bgColor }} variant="rounded">
                {isExcluded ? (
                  <Icon name="visibility_off" sx={{ color: 'text.primary' }} />
                ) : (
                  <Icon name="visibility" sx={{ color: 'text.primary' }} />
                )}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              id="hide-transaction-text"
              primary={copy.exclude_transaction}
              secondary={copy.excluding_transaction}
            />
            <ListItemIcon aria-hidden="true">
              <Switch
                checked={isExcluded}
                slotProps={{ input: { tabIndex: -1 } }}
                sx={{ pointerEvents: 'none' }}
                tabIndex={-1}
              />
            </ListItemIcon>
          </ListItemButton>

          <Dialog
            copy={{
              title: isExcluded ? copy.include_transaction : copy.exclude_transaction,
            }}
            isOpen={open}
            onClose={() => {
              setShouldDisableDrawerScroll(false)
              setOpen(false)
            }}
            onPrimaryAction={toggleExclude}
            onSecondaryAction={() =>
              onEvent(
                isExcluded
                  ? ON_CLICK_EVENTS.ON_TRANSACTION_UNHIDE_CANCEL_CLICK
                  : ON_CLICK_EVENTS.ON_TRANSACTION_HIDE_CANCEL_CLICK,
              )
            }
            primaryText={isExcluded ? copy.include : copy.exclude}
            secondaryText={commonCopy.cancel_button}
          >
            <Text truncate={false} variant="subtitle1">
              {isExcluded ? copy.including_transaction : copy.excluding_transaction}
            </Text>
          </Dialog>
        </ListItem>
      )}
    </React.Fragment>
  )
}

export default observer(ExcludeAction)
